import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import api from "../utils/api";
import ChatWidget from "./ChatWidget";
import ApplyModal from "./ApplyModal";
import { openRegistrationForm } from "../utils/registrationForm";
import { DISCORD_LINK } from "./DiscordPage/data/DiscordPageData";

const GOOGLE_SCRIPT_ID = "google-identity-services";

const loadGoogleIdentityScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

/* ── Helpers ─────────────────────────────────────────────────────────────────── */
function initials(str = "") {
  const parts = str.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return str.slice(0, 2).toUpperCase();
}

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    cls: "badge-orange",
    desc: "Under review by our team.",
  },
  reviewed: {
    label: "Reviewed",
    cls: "badge-blue",
    desc: "Our team has reviewed your application.",
  },
  accepted: {
    label: "Accepted",
    cls: "badge-green",
    desc: "Congratulations! You've been accepted.",
  },
  rejected: {
    label: "Rejected",
    cls: "badge-red",
    desc: "Unfortunately this role was not a fit.",
  },
};

function fmtTime(d) {
  return new Date(d).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}
function fmtDate(d) {
  const dt = new Date(d),
    now = new Date(),
    yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (dt.toDateString() === now.toDateString()) return "Today";
  if (dt.toDateString() === yest.toDateString()) return "Yesterday";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isLogicSignatureComplete(profile) {
  if (!profile) return false;
  if (typeof profile.logicSignatureComplete === "boolean") {
    return profile.logicSignatureComplete;
  }
  return Boolean(profile.githubConnected && profile.discordConnected);
}

/* ── Home button icon ────────────────────────────────────────────────────────── */
const HomeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

/* ── Chat sounds — synthesized with WebAudio, no audio files needed ──────────── */
let chatAudioCtx = null;
const chatTone = (freq, startAt, duration, { gain = 0.05, glideTo } = {}) => {
  const ctx = chatAudioCtx;
  const t0 = ctx.currentTime + startAt;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + duration);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
};
const playChatSound = (kind) => {
  try {
    chatAudioCtx =
      chatAudioCtx ||
      new (window.AudioContext || window.webkitAudioContext)();
    if (chatAudioCtx.state === "suspended") chatAudioCtx.resume().catch(() => {});
    if (kind === "send") {
      // quick upward swoosh
      chatTone(620, 0, 0.1, { gain: 0.05, glideTo: 1050 });
    } else {
      // soft two-note "message in"
      chatTone(540, 0, 0.09, { gain: 0.04 });
      chatTone(810, 0.08, 0.14, { gain: 0.045 });
    }
  } catch {}
};

const CHAT_STYLES = `
  .ql-chat-scroll{scroll-behavior:smooth;overscroll-behavior:contain;}
  .ql-chat-scroll::-webkit-scrollbar{width:8px;}
  .ql-chat-scroll::-webkit-scrollbar-track{background:transparent;}
  .ql-chat-scroll::-webkit-scrollbar-thumb{background:var(--line-2,rgba(255,255,255,.12));border-radius:8px;}
  .ql-chat-scroll::-webkit-scrollbar-thumb:hover{background:rgba(79,142,247,.5);}
  @keyframes ql-msg-in{from{opacity:0;transform:translateY(10px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  .ql-msg{animation:ql-msg-in .24s cubic-bezier(.16,1,.3,1) both;}
  @keyframes ql-typing-bounce{0%,60%,100%{transform:translateY(0);opacity:.35;}30%{transform:translateY(-4px);opacity:1;}}
  .ql-typing-dot{animation:ql-typing-bounce 1.2s infinite;}
  .ql-chat-input{transition:border-color .15s,box-shadow .15s;}
  .ql-chat-input:focus{border-color:rgba(79,142,247,.55)!important;box-shadow:0 0 0 3px rgba(79,142,247,.14);}
  .ql-send-btn{transition:transform .12s,opacity .14s,box-shadow .15s;}
  .ql-send-btn:not(:disabled):hover{transform:scale(1.07);box-shadow:0 4px 16px rgba(79,142,247,.45);}
  .ql-send-btn:not(:disabled):active{transform:scale(.92);}
`;

/* ── Inline full-height chat (Messages tab) ──────────────────────────────────── */
function InlineChat() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  // While > 0, the AI interviewer is "typing" — show the indicator until then.
  const [typingUntil, setTypingUntil] = useState(0);
  // Optimistic copy of the message being sent. While it's in flight the
  // server is generating the bot's reply, so the typing dots show right away
  // instead of only after /chat/send returns.
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);
  // Real message id → optimistic key, so the bubble isn't remounted (and its
  // entry animation replayed) when the server copy replaces the pending one.
  const keyMapRef = useRef({});
  const interviewActiveRef = useRef(false);
  const scrollAreaRef = useRef(null);
  const textareaRef = useRef(null);
  const pollRef = useRef(null);
  const didInitialScroll = useRef(false);
  const shouldScrollAfterSend = useRef(false);
  // Mirror of the messages state + timestamp of the newest one, so polls only
  // fetch what's new instead of re-downloading and re-rendering everything.
  const messagesRef = useRef([]);
  const lastSeenRef = useRef(null);

  const isNearBottom = () => {
    const el = scrollAreaRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };

  const scrollToBottom = (smooth = true) => {
    const el = scrollAreaRef.current;
    if (el)
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
  };

  const appendMessages = useCallback((incoming, { sound = true } = {}) => {
    const seen = new Set(messagesRef.current.map((m) => m._id));
    const fresh = incoming.filter((m) => m && m._id && !seen.has(m._id));
    if (fresh.length === 0) return;

    // A poll can bring back the server copy of the message still being sent.
    const p = pendingRef.current;
    const real =
      p && fresh.find((m) => m.senderRole === "user" && m.text === p.text);
    if (real) {
      keyMapRef.current[real._id] = p._id;
      pendingRef.current = null;
      setPending(null);
    }

    const stick = isNearBottom();
    messagesRef.current = [...messagesRef.current, ...fresh];
    lastSeenRef.current =
      messagesRef.current[messagesRef.current.length - 1].createdAt;
    setMessages(messagesRef.current);

    if (sound && fresh.some((m) => m.senderRole === "admin"))
      playChatSound("receive");
    if (stick || shouldScrollAfterSend.current) {
      shouldScrollAfterSend.current = false;
      requestAnimationFrame(() => scrollToBottom(true));
    }
  }, []);

  const load = useCallback(
    async (silent) => {
      if (!silent) setLoading(true);
      try {
        const after = lastSeenRef.current;
        const { data } = await api.get(
          `/chat/mine${after ? `?after=${encodeURIComponent(after)}` : ""}`,
        );
        if (!Array.isArray(data)) return;
        if (after) {
          appendMessages(data);
        } else {
          messagesRef.current = data;
          lastSeenRef.current = data.length
            ? data[data.length - 1].createdAt
            : null;
          setMessages(data);
        }
      } catch {
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [appendMessages],
  );

  const refreshInterviewStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/interview/status");
      interviewActiveRef.current = Boolean(data.active);
      if (data.botTypingUntil) {
        const until = new Date(data.botTypingUntil).getTime();
        if (until > Date.now()) setTypingUntil(until);
      }
    } catch {}
  }, []);

  useEffect(() => {
    load(false);
    refreshInterviewStatus();
  }, [load, refreshInterviewStatus]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      load(true);
      if (interviewActiveRef.current) refreshInterviewStatus();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [load, refreshInterviewStatus]);

  // When the bot "finishes typing", clear the indicator and pull its message.
  useEffect(() => {
    if (!typingUntil) return;
    requestAnimationFrame(() => scrollToBottom(true));
    const t = setTimeout(
      () => {
        setTypingUntil(0);
        shouldScrollAfterSend.current = true;
        load(true);
      },
      Math.max(typingUntil - Date.now(), 0) + 500,
    );
    return () => clearTimeout(t);
  }, [typingUntil, load]);

  // Open at the latest message once, then leave scroll position under user control.
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea || messages.length === 0) return;

    if (!didInitialScroll.current) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
      didInitialScroll.current = true;
      return;
    }

    if (shouldScrollAfterSend.current) {
      shouldScrollAfterSend.current = false;
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    const optimistic = {
      _id: `pending-${Date.now()}`,
      senderRole: "user",
      text: t,
      createdAt: new Date().toISOString(),
      pending: true,
    };
    pendingRef.current = optimistic;
    setPending(optimistic);
    requestAnimationFrame(() => scrollToBottom(true));

    try {
      const { data } = await api.post("/chat/send", { text: t });
      playChatSound("send");
      shouldScrollAfterSend.current = true;
      if (pendingRef.current) keyMapRef.current[data._id] = optimistic._id;
      appendMessages([data], { sound: false });
      if (data.botTypingUntil) {
        const until = new Date(data.botTypingUntil).getTime();
        if (until > Date.now()) setTypingUntil(until);
      }
    } catch {
      // Not saved — give the text back so the user can retry.
      setText(t);
    } finally {
      pendingRef.current = null;
      setPending(null);
      setSending(false);
    }
  };

  // Dots while the reply is being generated (request in flight) and while the
  // bot is "typing" it out (until deliverAt).
  const botTyping = typingUntil > 0 || sending;

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const shown = pending ? [...messages, pending] : messages;
  const grouped = shown.reduce((acc, msg) => {
    const d = fmtDate(msg.createdAt);
    if (!acc.length || acc[acc.length - 1].date !== d)
      acc.push({ date: d, msgs: [] });
    acc[acc.length - 1].msgs.push(msg);
    return acc;
  }, []);

  return (
    <>
      <style>{CHAT_STYLES}</style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "13px 16px",
          background:
            "linear-gradient(135deg,rgba(79,142,247,.14),rgba(124,58,237,.12))",
          backdropFilter: "blur(16px) saturate(160%)",
          WebkitBackdropFilter: "blur(16px) saturate(160%)",
          borderBottom: "1px solid rgba(255,255,255,.07)",
          boxShadow: "0 1px 0 rgba(255,255,255,.04) inset",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#4f8ef7,#7c3aed)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: ".74rem",
            fontWeight: 800,
            color: "#fff",
            position: "relative",
            flexShrink: 0,
          }}
        >
          QL
          <div
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#22c55e",
              border: "2px solid var(--surface)",
            }}
          />
        </div>
        <div>
          <div
            style={{
              fontSize: ".9rem",
              fontWeight: 700,
              color: "var(--text-1)",
              fontFamily: "var(--display)",
            }}
          >
            Quantum Team
          </div>
          <div
            style={{
              fontSize: ".7rem",
              color: "#22c55e",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginTop: 1,
            }}
          >
            <svg width="7" height="7" viewBox="0 0 8 8" fill="#22c55e">
              <circle cx="4" cy="4" r="4" />
            </svg>
            Online · typically replies fast
          </div>
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        className="ql-chat-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 16px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 0,
        }}
      >
        {loading && messages.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ display: "flex", gap: 5 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "var(--blue)",
                    opacity: 0.4,
                    animation: `typing-dot 1.4s ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        ) : shown.length === 0 && !botTyping ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: "rgba(79,142,247,.08)",
                border: "1px solid rgba(79,142,247,.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--blue)",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p
              style={{
                fontSize: ".8125rem",
                lineHeight: 1.6,
                color: "var(--text-2)",
                maxWidth: 240,
                margin: 0,
              }}
            >
              Send us a message — our team typically responds within a few
              hours.
            </p>
          </div>
        ) : (
          grouped.map((g) => (
            <div
              key={g.date}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: ".66rem",
                  color: "var(--text-3)",
                  fontWeight: 700,
                  letterSpacing: ".07em",
                  textTransform: "uppercase",
                }}
              >
                <div
                  style={{ flex: 1, height: 1, background: "var(--line)" }}
                />
                {g.date}
                <div
                  style={{ flex: 1, height: 1, background: "var(--line)" }}
                />
              </div>
              {g.msgs.map((msg) => {
                const isMe = msg.senderRole === "user";
                return (
                  <div
                    key={keyMapRef.current[msg._id] || msg._id}
                    className="ql-msg"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "75%",
                      alignSelf: isMe ? "flex-end" : "flex-start",
                      alignItems: isMe ? "flex-end" : "flex-start",
                      opacity: msg.pending ? 0.7 : 1,
                      transition: "opacity .2s",
                    }}
                  >
                    {!isMe && (
                      <div
                        style={{
                          fontSize: ".63rem",
                          fontWeight: 700,
                          color: "var(--text-3)",
                          marginBottom: 3,
                          paddingLeft: 3,
                        }}
                      >
                        Quantum Team
                      </div>
                    )}
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: 16,
                        fontSize: ".875rem",
                        lineHeight: 1.55,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        background: isMe
                          ? "linear-gradient(135deg,rgba(79,142,247,.82),rgba(99,102,241,.82))"
                          : "rgba(255,255,255,.055)",
                        backdropFilter: "blur(14px) saturate(160%)",
                        WebkitBackdropFilter: "blur(14px) saturate(160%)",
                        color: isMe ? "#fff" : "var(--text-1)",
                        border: isMe
                          ? "1px solid rgba(255,255,255,.16)"
                          : "1px solid rgba(255,255,255,.09)",
                        boxShadow: isMe
                          ? "0 4px 18px rgba(79,142,247,.35), inset 0 1px 0 rgba(255,255,255,.22)"
                          : "0 4px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.07)",
                        borderBottomRightRadius: isMe ? 5 : 16,
                        borderBottomLeftRadius: isMe ? 16 : 5,
                      }}
                    >
                      {msg.text}
                    </div>
                    <div
                      style={{
                        fontSize: ".62rem",
                        color: "var(--text-3)",
                        marginTop: 3,
                        paddingLeft: 3,
                      }}
                    >
                      {fmtTime(msg.createdAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        {botTyping && (
          <div
            className="ql-msg"
            style={{
              display: "flex",
              flexDirection: "column",
              maxWidth: "75%",
              alignSelf: "flex-start",
              alignItems: "flex-start",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                fontSize: ".63rem",
                fontWeight: 700,
                color: "var(--text-3)",
                marginBottom: 3,
                paddingLeft: 3,
              }}
            >
              Quantum Team
            </div>
            <div
              style={{
                padding: "13px 16px",
                borderRadius: 16,
                borderBottomLeftRadius: 5,
                background: "rgba(255,255,255,.055)",
                backdropFilter: "blur(14px) saturate(160%)",
                WebkitBackdropFilter: "blur(14px) saturate(160%)",
                border: "1px solid rgba(255,255,255,.09)",
                boxShadow:
                  "0 4px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.07)",
                display: "flex",
                gap: 5,
              }}
            >
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="ql-typing-dot"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "var(--text-3)",
                    animationDelay: `${i * 0.18}s`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          padding: "11px 14px 14px",
          borderTop: "1px solid rgba(255,255,255,.07)",
          background: "rgba(255,255,255,.028)",
          backdropFilter: "blur(16px) saturate(160%)",
          WebkitBackdropFilter: "blur(16px) saturate(160%)",
          flexShrink: 0,
        }}
      >
        <textarea
          ref={textareaRef}
          className="ql-chat-input"
          style={{
            flex: 1,
            resize: "none",
            background: "rgba(255,255,255,.05)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,.1)",
            borderRadius: 12,
            padding: "10px 13px",
            fontFamily: "var(--body)",
            fontSize: ".875rem",
            color: "var(--text-1)",
            outline: "none",
            minHeight: 42,
            maxHeight: 140,
            lineHeight: 1.5,
          }}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
          }}
          onKeyDown={handleKey}
          rows={1}
          disabled={sending}
        />
        <button
          className="ql-send-btn"
          style={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 12,
            background:
              "linear-gradient(135deg,rgba(79,142,247,.9),rgba(99,102,241,.9))",
            border: "1px solid rgba(255,255,255,.18)",
            boxShadow:
              "0 4px 14px rgba(79,142,247,.3), inset 0 1px 0 rgba(255,255,255,.25)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            opacity: !text.trim() || sending ? 0.3 : 1,
          }}
          onClick={send}
          disabled={!text.trim() || sending}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </>
  );
}

/* ── Logic Signature Form ─────────────────────────────────────────────────── */
function LogicSignatureForm({ acceptedJobTitle, profile, notify }) {
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [error, setError] = useState("");

  const connectProvider = async (provider) => {
    setError("");
    setLoadingProvider(provider);
    try {
      const { data } = await api.get(`/users/me/signature/${provider}/start`);
      window.location.assign(data.url);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          `Failed to start ${provider} authorization.`,
      );
      setLoadingProvider(null);
    }
  };

  const providers = [
    {
      id: "github",
      name: "GitHub",
      label: "Authorize with GitHub",
      helper: "Verify your developer profile",
      connected: !!profile?.githubConnected,
      value: profile?.githubUrl,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
        </svg>
      ),
    },
    {
      id: "discord",
      name: "Discord",
      label: "Authorize with Discord",
      helper: "Save your Discord username and account ID",
      connected: !!profile?.discordConnected,
      value: profile?.discordUrl,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37A19.8 19.8 0 0016.558 3a13.7 13.7 0 00-.48.98 18.4 18.4 0 00-5.156 0A12.9 12.9 0 0010.442 3c-1.32.24-2.59.7-3.759 1.37C4.305 7.92 3.66 11.38 3.98 14.79a19.9 19.9 0 004.607 2.34c.37-.5.7-1.03.982-1.59a12.9 12.9 0 01-1.55-.75l.37-.29c2.987 1.38 6.226 1.38 9.177 0l.37.29c-.49.29-1.008.54-1.55.75.282.56.612 1.09.982 1.59a19.8 19.8 0 004.61-2.34c.38-3.96-.65-7.39-2.66-10.42zM9.35 12.69c-.897 0-1.632-.82-1.632-1.83s.72-1.83 1.632-1.83c.92 0 1.648.83 1.632 1.83 0 1.01-.72 1.83-1.632 1.83zm5.3 0c-.897 0-1.632-.82-1.632-1.83s.72-1.83 1.632-1.83c.92 0 1.648.83 1.632 1.83 0 1.01-.72 1.83-1.632 1.83z" />
        </svg>
      ),
    },
  ];

  const bothConnected = providers.every((p) => p.connected);

  return (
    <div className="logic-signature-panel">
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: ".875rem",
          marginBottom: "1.25rem",
        }}
      >
        <div className="logic-signature-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
        <div>
          <h4
            style={{
              fontFamily: "var(--display)",
              fontSize: "1rem",
              fontWeight: 700,
              color: "var(--text-1)",
              margin: "0 0 .25rem",
            }}
          >
            Complete Your Logic Signature
          </h4>
          <p
            style={{
              fontSize: ".8125rem",
              color: "var(--text-2)",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            You've been accepted for{" "}
            <strong style={{ color: "var(--text-1)" }}>
              {acceptedJobTitle}
            </strong>
            . Authorize GitHub and Discord so we can verify the exact accounts.
          </p>
        </div>
      </div>

      <div className="logic-provider-grid">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`logic-provider-card logic-provider-card--${provider.id}${
              provider.connected ? " is-connected" : ""
            }`}
          >
            <div className="logic-provider-head">
              <div className="logic-provider-title-wrap">
                <span className="logic-provider-icon">{provider.icon}</span>
                <div>
                  <div className="logic-provider-name">{provider.name}</div>
                  <div className="logic-provider-helper">{provider.helper}</div>
                </div>
              </div>
              {provider.connected && (
                <span className="logic-provider-badge">Connected</span>
              )}
            </div>
            {provider.connected ? (
              <div className="logic-provider-connected">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {provider.value}
              </div>
            ) : (
              <button
                className={`logic-provider-button logic-provider-button--${provider.id}`}
                onClick={() => connectProvider(provider.id)}
                disabled={!!loadingProvider}
              >
                {loadingProvider === provider.id ? (
                  <span className="auth-spinner" />
                ) : (
                  <>
                    {provider.icon}
                    <span>{provider.label}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <line x1="7" y1="17" x2="17" y2="7" />
                      <polyline points="7 7 17 7 17 17" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="auth-error" style={{ marginTop: ".875rem" }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {bothConnected && (
        <div
          style={{
            marginTop: "1rem",
            color: "var(--green)",
            fontSize: ".875rem",
            fontWeight: 700,
          }}
        >
          Logic Signature complete.
        </div>
      )}
    </div>
  );
}

/* ── Edit Profile Form ────────────────────────────────────────────────────── */
function EditProfileForm({ profile, notify, onSuccess, children }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: profile?.name || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    linkedinUrl: profile?.linkedinUrl || "",
  });
  const [pwForm, setPwForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profErr, setProfErr] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [profLoad, setProfLoad] = useState(false);
  const [pwLoad, setPwLoad] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [emailPanel, setEmailPanel] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [emailInfo, setEmailInfo] = useState("");
  const [emailLoad, setEmailLoad] = useState(false);
  const googleEmailRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const currentEmail = (profile?.email || "").toLowerCase().trim();
  const nextEmail = (form.email || "").toLowerCase().trim();
  const emailChanged = !!nextEmail && nextEmail !== currentEmail;
  const emailIsGmail = /@gmail\.com$/i.test(nextEmail);

  useEffect(() => {
    if (profile)
      setForm({
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        linkedinUrl: profile.linkedinUrl || "",
      });
  }, [profile]);

  useEffect(() => {
    if (!emailChanged) {
      setEmailPanel(false);
      setEmailOtp("");
      setEmailErr("");
      setEmailInfo("");
    }
  }, [emailChanged]);

  const handleGoogleEmailCredential = useCallback(
    async ({ credential }) => {
      setEmailErr("");
      setEmailInfo("");
      setEmailLoad(true);
      try {
        const { data } = await api.post("/users/me/email-change/google", {
          email: nextEmail,
          credential,
        });
        notify("Email changed with Google verification.");
        onSuccess(data);
        setForm((f) => ({ ...f, email: data.email }));
        setEmailPanel(false);
        setEmailOtp("");
        setEditMode(false);
      } catch (err) {
        setEmailErr(
          err.response?.data?.message ||
            "Google could not verify this email. Try OTP instead.",
        );
      } finally {
        setEmailLoad(false);
      }
    },
    [nextEmail, notify, onSuccess],
  );

  useEffect(() => {
    if (!editMode || !emailPanel || !emailIsGmail || !googleClientId) return;

    let cancelled = false;
    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !googleEmailRef.current) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleEmailCredential,
          auto_select: false,
        });
        requestAnimationFrame(() => {
          if (cancelled || !googleEmailRef.current) return;
          googleEmailRef.current.innerHTML = "";
          window.google.accounts.id.renderButton(googleEmailRef.current, {
            type: "standard",
            theme: "filled_black",
            size: "large",
            shape: "pill",
            text: "continue_with",
            logo_alignment: "left",
            width: googleEmailRef.current.clientWidth || 280,
          });
        });
      })
      .catch(() => setEmailErr("Could not load Google verification."));

    return () => {
      cancelled = true;
    };
  }, [
    editMode,
    emailPanel,
    emailIsGmail,
    googleClientId,
    handleGoogleEmailCredential,
  ]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value }));
    if (k === "email") {
      setEmailErr("");
      setEmailInfo("");
      setEmailOtp("");
    }
  };
  const setPw = (k) => (e) => setPwForm((f) => ({ ...f, [k]: e.target.value }));

  const resetProfileDraft = () => {
    setForm({
      name: profile?.name || "",
      email: profile?.email || "",
      phone: profile?.phone || "",
      linkedinUrl: profile?.linkedinUrl || "",
    });
    setProfErr("");
    setEmailPanel(false);
    setEmailOtp("");
    setEmailErr("");
    setEmailInfo("");
  };

  const sendEmailOtp = async () => {
    setEmailErr("");
    setEmailInfo("");
    if (!nextEmail) return setEmailErr("Enter the new email first.");
    setEmailLoad(true);
    try {
      const { data } = await api.post("/users/me/email-change/otp/start", {
        email: nextEmail,
      });
      setEmailPanel(true);
      setEmailInfo(data.message || "Verification code sent.");
    } catch (err) {
      setEmailErr(
        err.response?.data?.message || "Could not send verification code.",
      );
    } finally {
      setEmailLoad(false);
    }
  };

  const verifyEmailOtp = async () => {
    setEmailErr("");
    setEmailInfo("");
    if (emailOtp.trim().length !== 6)
      return setEmailErr("Enter the 6-digit verification code.");
    setEmailLoad(true);
    try {
      const { data } = await api.post("/users/me/email-change/otp/verify", {
        email: nextEmail,
        otp: emailOtp.trim(),
      });
      notify("Email changed after OTP verification.");
      onSuccess(data);
      setForm((f) => ({ ...f, email: data.email }));
      setEmailPanel(false);
      setEmailOtp("");
      setEditMode(false);
    } catch (err) {
      setEmailErr(err.response?.data?.message || "Could not verify email.");
    } finally {
      setEmailLoad(false);
    }
  };

  const saveProfile = async () => {
    setProfErr("");
    if (!form.name.trim()) return setProfErr("Name is required.");
    setProfLoad(true);
    try {
      const { data } = await api.patch("/users/me", {
        name: form.name,
        phone: form.phone,
        linkedinUrl: form.linkedinUrl,
      });
      if (emailChanged) {
        setEmailPanel(true);
        setProfErr("Verify the new email below to finish changing it.");
        notify("Profile details saved. Verify your new email to update it.");
      } else {
        onSuccess(data);
        notify("Profile updated successfully.");
        setEditMode(false);
      }
    } catch (err) {
      setProfErr(err.response?.data?.message || "Failed to update profile.");
    } finally {
      setProfLoad(false);
    }
  };

  const changePassword = async () => {
    setPwErr("");
    if (
      !pwForm.currentPassword ||
      !pwForm.newPassword ||
      !pwForm.confirmPassword
    )
      return setPwErr("All fields are required.");
    if (pwForm.newPassword !== pwForm.confirmPassword)
      return setPwErr("New passwords do not match.");
    if (pwForm.newPassword.length < 6)
      return setPwErr("Password must be at least 6 characters.");
    setPwLoad(true);
    try {
      await api.patch("/users/me/password", pwForm);
      notify("Password changed successfully.");
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPwErr(err.response?.data?.message || "Failed to change password.");
    } finally {
      setPwLoad(false);
    }
  };

  return (
    <>
      <div className="card" style={{ marginTop: "1.25rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.25rem",
          }}
        >
          <h4 className="card-title" style={{ margin: 0 }}>
            Profile Information
          </h4>
          {!editMode ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEditMode(true)}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setEditMode(false);
                resetProfileDraft();
              }}
            >
              Cancel
            </button>
          )}
        </div>
        {!editMode ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1.25rem",
            }}
          >
            {[
              ["Full Name", form.name || "—"],
              ["Email", form.email || "—"],
              ["Phone", form.phone || "—"],
              ["LinkedIn", form.linkedinUrl || "—"],
            ].map(([l, v]) => (
              <div key={l}>
                <span
                  style={{
                    fontSize: ".72rem",
                    fontWeight: 700,
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "var(--text-3)",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  {l}
                </span>
                <span style={{ fontSize: ".9rem", color: "var(--text-1)" }}>
                  {v}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: ".875rem",
              }}
            >
              {[
                ["Full Name", "name", "text", "Your full name"],
                ["Email Address", "email", "email", "you@example.com"],
                ["Phone", "phone", "text", "+92 300 0000000"],
                ["LinkedIn", "linkedinUrl", "text", "linkedin.com/in/yourname"],
              ].map(([l, k, t, p]) => (
                <div key={k} className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{l}</label>
                  <input
                    className="form-input"
                    type={t}
                    placeholder={p}
                    value={form[k]}
                    onChange={set(k)}
                  />
                </div>
              ))}
            </div>
            {emailChanged && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  border: "1px solid rgba(79, 142, 247, .28)",
                  borderRadius: 12,
                  background:
                    "linear-gradient(135deg, rgba(79,142,247,.12), rgba(17,24,39,.18))",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: ".9rem",
                        fontWeight: 800,
                        color: "var(--text-1)",
                        marginBottom: 4,
                      }}
                    >
                      Verify new email
                    </div>
                    <div
                      style={{
                        fontSize: ".8rem",
                        color: "var(--text-3)",
                        lineHeight: 1.6,
                      }}
                    >
                      {nextEmail} must be verified before it replaces your current
                      email.
                    </div>
                  </div>
                  {!emailPanel && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEmailPanel(true)}
                    >
                      Verify
                    </button>
                  )}
                </div>

                {emailPanel && (
                  <div style={{ marginTop: ".875rem" }}>
                    {emailIsGmail && googleClientId && (
                      <>
                        <div
                          ref={googleEmailRef}
                          className="auth-google-button"
                          style={{ maxWidth: 360, minHeight: 42 }}
                          aria-label="Continue with Google to verify email"
                        />
                        <div
                          className="auth-divider"
                          style={{ margin: ".875rem 0" }}
                        >
                          <span>or verify with code</span>
                        </div>
                      </>
                    )}

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: ".65rem",
                        alignItems: "center",
                      }}
                    >
                      <input
                        className="form-input"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="6-digit code"
                        value={emailOtp}
                        onChange={(e) =>
                          setEmailOtp(e.target.value.replace(/\D/g, ""))
                        }
                        style={{ flex: "1 1 180px" }}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={sendEmailOtp}
                        disabled={emailLoad}
                      >
                        Send OTP
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={verifyEmailOtp}
                        disabled={emailLoad}
                      >
                        {emailLoad ? <span className="auth-spinner" /> : "Verify"}
                      </button>
                    </div>

                    {emailInfo && (
                      <div
                        style={{
                          marginTop: ".75rem",
                          fontSize: ".8rem",
                          color: "var(--green-1)",
                        }}
                      >
                        {emailInfo}
                      </div>
                    )}
                    {emailErr && (
                      <div
                        className="auth-error"
                        style={{ marginTop: ".75rem" }}
                      >
                        {emailErr}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {profErr && (
              <div className="auth-error" style={{ marginTop: ".875rem" }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {profErr}
              </div>
            )}
            <button
              className="btn btn-primary"
              style={{ marginTop: "1rem", justifyContent: "center" }}
              onClick={saveProfile}
              disabled={profLoad}
            >
              {profLoad ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </>
        )}
      </div>
      {children}
      <div className="card" style={{ marginTop: "1.25rem" }}>
        <h4 className="card-title" style={{ marginBottom: "1.25rem" }}>
          Change Password
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: ".875rem",
          }}
        >
          <div
            className="form-group"
            style={{ marginBottom: 0, gridColumn: "1 / -1" }}
          >
            <label className="form-label">Current Password</label>
            <div className="form-input-wrap">
              <input
                className="form-input"
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                value={pwForm.currentPassword}
                onChange={setPw("currentPassword")}
                style={{ paddingRight: 42 }}
              />
              <button
                type="button"
                className="form-eye-btn"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
              >
                {showPw ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">New Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="Min. 6 characters"
              value={pwForm.newPassword}
              onChange={setPw("newPassword")}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Confirm New Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="••••••••"
              value={pwForm.confirmPassword}
              onChange={setPw("confirmPassword")}
            />
          </div>
        </div>
        {pwErr && (
          <div className="auth-error" style={{ marginTop: ".875rem" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {pwErr}
          </div>
        )}
        <button
          className="btn btn-ghost"
          style={{ marginTop: "1rem" }}
          onClick={changePassword}
          disabled={pwLoad}
        >
          {pwLoad ? <span className="auth-spinner" /> : "Update Password"}
        </button>
      </div>
    </>
  );
}

function DashboardLoadingSkeleton({ tab }) {
  const cardCount = tab === "profile" ? 2 : tab === "messages" ? 1 : 4;
  const title =
    tab === "applications"
      ? "Loading your applications"
      : tab === "messages"
        ? "Opening your messages"
        : tab === "profile"
          ? "Preparing your profile"
          : "Finding open positions";

  return (
    <div className={`dashboard-skeleton dashboard-skeleton--${tab}`}>
      <div className="skeleton-page-header">
        <div>
          <div className="skeleton-eyebrow shimmer" />
          <div className="skeleton-title shimmer" />
          <div className="skeleton-subtitle shimmer" />
        </div>
        <div className="skeleton-badge shimmer" />
      </div>

      <div className="skeleton-status-card">
        <span className="skeleton-status-spinner" />
        <div>
          <div className="skeleton-status-title">{title}</div>
          <div className="skeleton-status-text">
            Getting everything ready. This usually takes a moment.
          </div>
        </div>
      </div>

      <div className="skeleton-toolbar">
        <div className="skeleton-search shimmer" />
        <div className="skeleton-pill shimmer" />
        <div className="skeleton-pill shimmer" />
        <div className="skeleton-pill shimmer" />
      </div>

      {tab === "messages" ? (
        <div className="skeleton-chat-panel">
          <div className="skeleton-chat-sidebar">
            {[0, 1, 2].map((item) => (
              <div key={item} className="skeleton-chat-user shimmer" />
            ))}
          </div>
          <div className="skeleton-chat-window">
            <div className="skeleton-message skeleton-message--wide shimmer" />
            <div className="skeleton-message skeleton-message--short shimmer" />
            <div className="skeleton-message skeleton-message--self shimmer" />
            <div className="skeleton-chat-input shimmer" />
          </div>
        </div>
      ) : (
        <div className="skeleton-card-grid">
          {Array.from({ length: cardCount }).map((_, index) => (
            <div key={index} className="skeleton-content-card">
              <div className="skeleton-card-top">
                <div>
                  <div className="skeleton-line skeleton-line--tag shimmer" />
                  <div className="skeleton-line skeleton-line--title shimmer" />
                </div>
                <div className="skeleton-chip shimmer" />
              </div>
              <div className="skeleton-line skeleton-line--meta shimmer" />
              <div className="skeleton-line skeleton-line--body shimmer" />
              <div className="skeleton-line skeleton-line--body-short shimmer" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══ Main UserDashboard ══════════════════════════════════════════════════════ */
const dashboardTabFromUrl = () => {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return ["jobs", "applications", "messages", "profile"].includes(tab)
    ? tab
    : "jobs";
};

export default function UserDashboard({ user, logout, notify, onUserUpdate }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(dashboardTabFromUrl);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [jobFilter, setJobFilter] = useState("All");
  const [applyJob, setApplyJob] = useState(null);
  const [appStatusFilter, setAppStatusFilter] = useState("all");
  const [appSearch, setAppSearch] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const notifyRef = useRef(notify);
  const didInitialLoadRef = useRef(false);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const fetchData = useCallback(async ({ showSkeleton = false } = {}) => {
    if (showSkeleton) setLoading(true);
    // Each request is applied independently and failures are retried, so one
    // flaky response can't blank out the whole dashboard (e.g. hide the
    // accepted state and Logic Signature section).
    const attempt = async () => {
      const [jobsRes, appsRes, profileRes] = await Promise.allSettled([
        api.get("/jobs"),
        api.get("/applications/mine"),
        api.get("/users/me"),
      ]);
      if (jobsRes.status === "fulfilled") setJobs(jobsRes.value.data);
      if (appsRes.status === "fulfilled") setApplications(appsRes.value.data);
      if (profileRes.status === "fulfilled") setProfile(profileRes.value.data);
      return [jobsRes, appsRes, profileRes].every(
        (r) => r.status === "fulfilled",
      );
    };
    try {
      let ok = await attempt();
      for (let retry = 1; !ok && retry <= 2; retry++) {
        await new Promise((resolve) => setTimeout(resolve, 1500 * retry));
        ok = await attempt();
      }
      if (!ok) notifyRef.current("Failed to load data. Please refresh.", "error");
    } finally {
      if (showSkeleton) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    fetchData({ showSkeleton: true });
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get("tab");
    const signature = params.get("signature");
    if (["jobs", "applications", "messages", "profile"].includes(urlTab)) {
      setTab(urlTab);
    }
    if (signature) {
      const [provider, status] = signature.split(":");
      notify(
        status === "connected"
          ? `${provider === "github" ? "GitHub" : "Discord"} connected.`
          : `Could not connect ${provider}. Please try again.`,
        status === "connected" ? "success" : "error",
      );
      fetchData();
      setTab("profile");
      navigate("/dashboard?tab=profile", { replace: true });
    }
  }, [navigate, notify, fetchData]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const close = (e) => {
      if (!e.target.closest(".sidebar") && !e.target.closest(".sidebar-toggle"))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [sidebarOpen]);

  const navigateTab = (newTab) => {
    setSidebarOpen(false);
    setTab(newTab);
    if (newTab === "applications" || newTab === "profile") {
      fetchData();
    }
  };

  const applicationForJob = (jobId) =>
    applications.find((a) => String(a.jobId) === String(jobId));
  const hasActiveApplication = (jobId) => {
    const app = applicationForJob(jobId);
    return app && app.status !== "rejected";
  };
  const activeJobForApplication = (app) =>
    jobs.find((j) => String(j._id) === String(app.jobId));
  const upsertApplication = (app) =>
    setApplications((prev) => {
      const exists = prev.some((a) => a._id === app._id);
      return exists
        ? prev.map((a) => (a._id === app._id ? app : a))
        : [app, ...prev];
    });
  const depts = ["All", ...new Set(jobs.map((j) => j.department))];
  const filteredJobs = jobs.filter((j) => {
    const matchDept = jobFilter === "All" || j.department === jobFilter;
    const matchSearch =
      !jobSearch ||
      j.title.toLowerCase().includes(jobSearch.toLowerCase()) ||
      j.department.toLowerCase().includes(jobSearch.toLowerCase());
    return matchDept && matchSearch;
  });

  // One printable registration form covering every position applied for.
  const downloadRegistrationForm = async () => {
    setFormLoading(true);
    try {
      const count = await openRegistrationForm(profile || user, applications);
      notify(
        count > 1
          ? `Registration form downloaded — all ${count} positions included.`
          : "Registration form downloaded.",
      );
    } catch {
      notify("Could not generate the registration form.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const acceptedApps = applications.filter((a) => a.status === "accepted");
  const acceptedApp = acceptedApps[0];
  const isAccepted =
    acceptedApps.length > 0 || !!profile?.hasAcceptedApplication;
  const acceptedJobTitles = [
    ...new Set(
      [
        ...acceptedApps
          .map((a) => String(a.jobTitle || "").trim())
          .filter(Boolean),
        profile?.acceptedJobTitle
          ? String(profile.acceptedJobTitle).trim()
          : "",
      ].filter(Boolean),
    ),
  ];
  const displayAcceptedJobTitle =
    acceptedApp?.jobTitle || profile?.acceptedJobTitle || "your role";
  const isVerified = isLogicSignatureComplete(profile);
  const showLogicSignature =
    profile?.needsLogicSignature ?? (isAccepted && !isVerified);

  const pendingCount = applications.filter(
    (a) => a.status === "pending",
  ).length;
  const reviewedCount = applications.filter(
    (a) => a.status === "reviewed",
  ).length;
  const acceptedCount = applications.filter(
    (a) => a.status === "accepted",
  ).length;
  const rejectedCount = applications.filter(
    (a) => a.status === "rejected",
  ).length;

  const appFilterPills = [
    { value: "all", label: `All (${applications.length})` },
    { value: "pending", label: `Pending (${pendingCount})` },
    { value: "reviewed", label: `Reviewed (${reviewedCount})` },
    { value: "accepted", label: `Accepted (${acceptedCount})` },
    { value: "rejected", label: `Rejected (${rejectedCount})` },
  ];

  const filteredApps = applications.filter((a) => {
    const matchStatus =
      appStatusFilter === "all" || a.status === appStatusFilter;
    const q = appSearch.toLowerCase().trim();
    const matchSearch =
      !q ||
      (a.jobTitle || "").toLowerCase().includes(q) ||
      (a.status || "").toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const LABELS = {
    jobs: "Open Positions",
    applications: "My Applications",
    messages: "Messages",
    profile: "Profile",
  };

  const navItems = [
    { id: "jobs", icon: "briefcase", label: "Open Positions", badge: 0 },
    {
      id: "applications",
      icon: "eye",
      label: "My Applications",
      badge: pendingCount,
    },
    { id: "messages", icon: "chat", label: "Messages", badge: 0 },
    { id: "profile", icon: "users", label: "Profile", badge: 0 },
  ];

  const homeBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 13px",
    borderRadius: 9,
    background: "rgba(79,142,247,.08)",
    border: "1px solid rgba(79,142,247,.2)",
    color: "var(--blue)",
    fontSize: ".82rem",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "var(--body)",
    transition: "background .13s, border-color .13s",
    whiteSpace: "nowrap",
  };

  return (
    <div className="dashboard">
      {applyJob && user && (
        <ApplyModal
          job={applyJob}
          user={profile || user}
          onClose={() => setApplyJob(null)}
          notify={notify}
          onUserUpdate={(u) => {
            setProfile((prev) => ({ ...prev, ...u }));
            onUserUpdate?.(u);
          }}
          onSubmitted={upsertApplication}
          onStartInterview={() => {
            setApplyJob(null);
            navigateTab("messages");
          }}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
        <div className="sidebar-top">
          <img
            src="/logo.png"
            alt="Quantum Logics"
            width="26"
            height="26"
            style={{ borderRadius: 6, objectFit: "contain" }}
          />
          <span className="sidebar-brand">Quantum Logics</span>
        </div>
        <div className="sidebar-section-label">Menu</div>
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`sidebar-btn${tab === item.id ? " active" : ""}`}
            onClick={() => navigateTab(item.id)}
          >
            <Icon name={item.icon} size={16} />
            {item.label}
            {item.badge > 0 && (
              <span className="sidebar-badge sidebar-badge-secondary">
                {item.badge}
              </span>
            )}
          </button>
        ))}
        <div className="sidebar-footer">
          <button
            style={{
              ...homeBtnStyle,
              width: "100%",
              justifyContent: "center",
              marginBottom: ".625rem",
            }}
            onClick={() => navigate("/")}
          >
            <HomeIcon /> Go to Home
          </button>
          <div className="sidebar-user">
            <div className="sidebar-user-name">
              {profile?.name || user.name}
            </div>
            <div className="sidebar-user-email">
              {profile?.email || user.email}
            </div>
          </div>
          <button className="sidebar-btn sidebar-logout" onClick={logout}>
            <Icon name="logout" size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="main-content">
        {/* Mobile topbar */}
        <div className="dash-topbar">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <Icon name="menu" size={18} />
          </button>
          <span className="dash-topbar-title">
            {LABELS[tab] || "Dashboard"}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            <button style={homeBtnStyle} onClick={() => navigate("/")}>
              <HomeIcon /> Home
            </button>
            <button className="sidebar-toggle" onClick={logout}>
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>

        <div key={tab} className="tab-content">
          {loading && (
            <DashboardLoadingSkeleton tab={tab} />
          )}

          {/* ── Open Positions ── */}
          {!loading && tab === "jobs" && (
            <>
              <div className="page-header page-header--row">
                <div>
                  <h1>Open Positions</h1>
                  <p>Browse and apply for available roles.</p>
                </div>
                <span className="badge badge-blue">
                  {filteredJobs.length} role
                  {filteredJobs.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="dash-jobs-filters">
                <div className="jobs-search-wrap" style={{ flex: 1 }}>
                  <svg
                    className="jobs-search-icon"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    className="jobs-search"
                    placeholder="Search roles…"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                  />
                </div>
                <div className="jobs-filter-group" style={{ flexWrap: "wrap" }}>
                  {depts.map((d) => (
                    <button
                      key={d}
                      className={`jobs-filter-btn${jobFilter === d ? " active" : ""}`}
                      onClick={() => setJobFilter(d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="user-jobs-grid">
                {filteredJobs.map((job) => (
                  <div key={job._id} className="user-job-card">
                    <div className="user-job-card-top">
                      <div>
                        <span className="job-dept-tag">{job.department}</span>
                        <h4 className="user-job-title">{job.title}</h4>
                        <div className="user-job-meta">
                          <span>
                            <Icon name="location" size={12} />
                            {job.location}
                          </span>
                          <span>
                            <Icon name="clock" size={12} />
                            {job.type}
                          </span>
                          {job.salary && (
                            <span>
                              <Icon name="dollar" size={12} />
                              {job.salary}
                            </span>
                          )}
                        </div>
                      </div>
                      {hasActiveApplication(job._id) ? (
                        <span className="badge badge-green">Applied</span>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setApplyJob(job)}
                        >
                          {applicationForJob(job._id)?.status === "rejected"
                            ? "Reapply"
                            : "Apply"}
                        </button>
                      )}
                    </div>
                    {job.description && (
                      <p className="user-job-desc">
                        {job.description.slice(0, 120)}…
                      </p>
                    )}
                  </div>
                ))}
                {filteredJobs.length === 0 && (
                  <div className="empty-state" style={{ gridColumn: "1/-1" }}>
                    <Icon name="briefcase" size={32} color="var(--text-3)" />
                    <p>No open positions match your search.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── My Applications ── */}
          {!loading && tab === "applications" && (
            <>
              <div className="page-header page-header--row">
                <div>
                  <h1>My Applications</h1>
                  <p>Track the status of all your submissions.</p>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                  }}
                >
                  {applications.length > 0 && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                      onClick={downloadRegistrationForm}
                      disabled={formLoading}
                      title="Download a registration form listing every position you've applied for"
                    >
                      {formLoading ? (
                        <span className="auth-spinner" />
                      ) : (
                        <>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Registration Form
                        </>
                      )}
                    </button>
                  )}
                  <span className="badge badge-blue">
                    {applications.length} total
                  </span>
                </div>
              </div>

              {applications.length === 0 ? (
                <div className="user-apps-empty">
                  <div className="user-apps-empty-icon">
                    <Icon name="briefcase" size={28} color="var(--text-3)" />
                  </div>
                  <h3>No applications yet</h3>
                  <p>Browse open roles and submit your first application.</p>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: "1rem" }}
                    onClick={() => navigateTab("jobs")}
                  >
                    Browse Open Roles
                  </button>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "center",
                    }}
                  >
                    <div className="jobs-search-wrap" style={{ flex: 1 }}>
                      <svg
                        className="jobs-search-icon"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        className="jobs-search"
                        placeholder="Search by job title or status…"
                        value={appSearch}
                        onChange={(e) => setAppSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div
                    className="jobs-filter-group"
                    style={{ flexWrap: "wrap", marginBottom: "1.25rem" }}
                  >
                    {appFilterPills.map((pill) => (
                      <button
                        key={pill.value}
                        className={`jobs-filter-btn${appStatusFilter === pill.value ? " active" : ""}`}
                        onClick={() => setAppStatusFilter(pill.value)}
                      >
                        {pill.label}
                      </button>
                    ))}
                  </div>

                  <div className="user-apps-list">
                    {filteredApps.length === 0 ? (
                      <div className="empty-state">
                        <Icon name="eye" size={28} color="var(--text-3)" />
                        <p>No applications match this filter.</p>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ marginTop: "0.75rem" }}
                          onClick={() => {
                            setAppStatusFilter("all");
                            setAppSearch("");
                          }}
                        >
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      filteredApps.map((a) => {
                        const sc = STATUS_CONFIG[a.status] || {
                          label: a.status,
                          cls: "badge-gray",
                          desc: "",
                        };
                        return (
                          <div
                            key={a._id}
                            className="user-app-card"
                            style={{
                              background: "var(--surface)",
                              border: "1px solid var(--line-2)",
                              borderRadius: 12,
                              padding: "1.25rem",
                              marginBottom: "0.875rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: "1.5rem",
                              }}
                            >
                              <div className="user-app-card-left">
                                <div className="user-app-title">
                                  {a.jobTitle}
                                </div>
                                <div className="user-app-date">
                                  Applied{" "}
                                  {new Date(a.appliedAt).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    },
                                  )}
                                </div>
                              </div>
                              <div className="user-app-card-right">
                                <span className={`badge ${sc.cls}`}>
                                  {sc.label}
                                </span>
                                {sc.desc && (
                                  <p className="user-app-status-desc">
                                    {sc.desc}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div
                              style={{
                                marginTop: "0.875rem",
                                paddingTop: "0.75rem",
                                borderTop: "1px solid var(--line-2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "0.75rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color: "var(--text-3)",
                                }}
                              >
                                {applications.length > 1
                                  ? `Covers all ${applications.length} of your applications`
                                  : "Signed record of your application"}
                              </span>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                                onClick={downloadRegistrationForm}
                                disabled={formLoading}
                                title="Download your registration form as a PDF"
                              >
                                {formLoading ? (
                                  <span className="auth-spinner" />
                                ) : (
                                  <>
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.2"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7 10 12 15 17 10" />
                                      <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Download Registration Form (PDF)
                                  </>
                                )}
                              </button>
                            </div>
                            {a.status === "accepted" && showLogicSignature && (
                              <div
                                style={{
                                  marginTop: "1rem",
                                  padding: "0.875rem 1rem",
                                  background: "rgba(239,68,68,0.08)",
                                  border: "1px solid rgba(239,68,68,0.28)",
                                  borderRadius: 10,
                                  color: "var(--red)",
                                  fontSize: "0.875rem",
                                  fontWeight: 700,
                                  lineHeight: 1.6,
                                }}
                              >
                                Go to Profile and complete your Logic Signature
                                to get verified.
                              </div>
                            )}
                            {a.status === "accepted" && isVerified && (
                              <div
                                style={{
                                  marginTop: "1rem",
                                  padding: "0.875rem 1rem",
                                  background: "rgba(34,197,94,0.06)",
                                  border: "1px solid rgba(34,197,94,0.2)",
                                  borderRadius: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "0.625rem",
                                  flexWrap: "wrap",
                                  fontSize: "0.875rem",
                                  color: "var(--green)",
                                  fontWeight: 600,
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.625rem",
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                  >
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                  </svg>
                                  Profile verified — Logic Signature complete!
                                </span>
                                <a
                                  href={DISCORD_LINK}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-primary btn-sm"
                                  style={{
                                    background: "#5865f2",
                                    borderColor: "#5865f2",
                                    color: "#fff",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Join Discord
                                </a>
                              </div>
                            )}
                            {a.status === "rejected" &&
                              activeJobForApplication(a) && (
                                <button
                                  className="btn btn-primary btn-sm"
                                  style={{ marginTop: "1rem" }}
                                  onClick={() =>
                                    setApplyJob(activeJobForApplication(a))
                                  }
                                >
                                  Reapply
                                </button>
                              )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Messages ── */}
          {!loading && tab === "messages" && (
            <>
              <div className="page-header">
                <h1>Messages</h1>
                <p>Chat directly with the Quantum Logics team.</p>
              </div>
              <div
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--line-2)",
                  borderRadius: 16,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100vh - 200px)",
                  minHeight: 400,
                }}
              >
                <InlineChat />
              </div>
            </>
          )}

          {/* ── Profile ── */}
          {!loading && tab === "profile" && (
            <div className="profile-page">
              <div className="page-header">
                <h1>My Profile</h1>
                <p>Manage your account details and security settings.</p>
              </div>
              <div className="profile-card">
                <div className="profile-avatar">
                  {initials(profile?.name || user.name || user.email)}
                </div>
                <div className="profile-info">
                  <h3 className="profile-name">{profile?.name || user.name}</h3>
                  <p className="profile-email">
                    {profile?.email || user.email}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {!isAccepted && (
                      <span
                        className="badge badge-blue"
                        style={{ display: "inline-flex" }}
                      >
                        Candidate
                      </span>
                    )}
                    {isAccepted && (
                      <span
                        className="badge badge-green"
                        style={{ display: "inline-flex" }}
                      >
                        Accepted
                      </span>
                    )}
                    {showLogicSignature && (
                      <span
                        className="badge badge-gray"
                        style={{ display: "inline-flex" }}
                      >
                        Unverified
                      </span>
                    )}
                    {acceptedJobTitles.map((title) => (
                      <span
                        key={title}
                        className="badge badge-blue"
                        style={{ display: "inline-flex" }}
                      >
                        {title}
                      </span>
                    ))}
                    {isVerified && (
                      <span
                        className="badge badge-green"
                        style={{
                          display: "inline-flex",
                          background: "rgba(34,197,94,0.15)",
                          gap: "4px",
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <EditProfileForm
                profile={profile}
                notify={notify}
                onSuccess={(u) => {
                  setProfile((prev) => ({ ...prev, ...u }));
                  onUserUpdate?.(u);
                }}
              >
                {showLogicSignature && (
                  <LogicSignatureForm
                    acceptedJobTitle={displayAcceptedJobTitle}
                    profile={profile}
                    notify={notify}
                  />
                )}
                {isVerified && (
                  <div className="card" style={{ marginTop: "1.25rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1rem",
                        flexWrap: "wrap",
                        marginBottom: "1rem",
                      }}
                    >
                      <h4 className="card-title" style={{ marginBottom: 0 }}>
                        Logic Signature
                      </h4>
                      <a
                        href={DISCORD_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                        style={{
                          background: "#5865f2",
                          borderColor: "#5865f2",
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Join Discord
                      </a>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: ".875rem",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: ".72rem",
                            fontWeight: 700,
                            letterSpacing: ".07em",
                            textTransform: "uppercase",
                            color: "var(--text-3)",
                            marginBottom: 4,
                          }}
                        >
                          GitHub
                        </div>
                        <a
                          href={profile.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: ".875rem", color: "var(--blue)" }}
                        >
                          {profile.githubUrl}
                        </a>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: ".72rem",
                            fontWeight: 700,
                            letterSpacing: ".07em",
                            textTransform: "uppercase",
                            color: "var(--text-3)",
                            marginBottom: 4,
                          }}
                        >
                          Discord Username
                        </div>
                        <span
                          style={{
                            fontSize: ".875rem",
                            color: "var(--text-1)",
                          }}
                        >
                          {profile.discordUrl}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </EditProfileForm>
              <div className="card" style={{ marginTop: "1.25rem" }}>
                <h4 className="card-title" style={{ marginBottom: "1rem" }}>
                  Activity
                </h4>
                <div className="profile-stats">
                  <div className="profile-stat">
                    <div className="profile-stat-num">
                      {applications.length}
                    </div>
                    <div className="profile-stat-label">Applications</div>
                  </div>
                  <div className="profile-stat">
                    <div className="profile-stat-num">{acceptedCount}</div>
                    <div className="profile-stat-label">Accepted</div>
                  </div>
                  <div className="profile-stat">
                    <div className="profile-stat-num">{pendingCount}</div>
                    <div className="profile-stat-label">Pending</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bottom nav */}
      <nav className="dash-bottom-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`dash-bottom-btn${tab === item.id ? " active" : ""}`}
            onClick={() => navigateTab(item.id)}
          >
            <span className="dash-bottom-icon">
              <Icon name={item.icon} size={19} />
              {item.badge > 0 && (
                <span className="dash-bottom-badge">{item.badge}</span>
              )}
            </span>
            <span className="dash-bottom-label">
              {LABELS[item.id] || item.label}
            </span>
          </button>
        ))}
      </nav>

      {tab !== "messages" && <ChatWidget user={user} asNavButton={false} />}
    </div>
  );
}
